import { useMutation } from '@apollo/client';
import type { UpdateActionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateActionDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateActionTRPCInput } from './useUpdateActionTRPC';
import { useUpdateActionTRPC } from './useUpdateActionTRPC';

export const useUpdateAction = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateActionGraphQL, graphqlState] = useMutation(
    UpdateActionDocument,
    {
      update: (cache) => {
        evictField(cache, 'action');
        evictField(cache, 'action_aggregate');
        evictField(cache, 'control');
      },
      refetchQueries: [namedOperations.Query.getActionById],
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateActionTRPC();

  const updateAction = async (
    variables: UpdateActionTRPCInput,
    options?: {
      confirmChangeRequest?: boolean;
      hasFileChanges?: boolean;
    }
  ): Promise<UpdateActionMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateAction(variables);
    }

    const result = await updateActionGraphQL({
      variables,
      context: {
        headers: {
          'x-confirm-change-request': options?.confirmChangeRequest
            ? 'true'
            : 'false',
          'x-has-file-changes': options?.hasFileChanges ? 'true' : 'false',
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to update action');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateAction,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateAction,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
